/*
 * Copyright (c) 2017, Indigen Solutions. All rights reserved.
 *
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 *
 * This software is the confidential and proprietary information.
 *
 * You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement
 * you entered into.
 */
/**
 *
 */
import { ProductHelper } from "./Product.helper";
import * as moment from "moment";
import { User } from "../models/User.model";
import { Customer } from "../types/Customer";
import { Quotation } from "../types/Quotation";
import { Item } from "../types/Item";
import { ProductGPI } from "../types/ProductGPI";
import { dicoGPI } from "../data/products/gpi/dico";

export class ProductGPIHelper extends ProductHelper {
    public defaultValue: ProductGPI = {
        isIAD: true,
        samplingInterval: "year",
        samplingDate: "08",
        beneficiaries: [{}],
    };

    public async init(advisor: User, customer: Customer, quotation: Quotation) {
        await this.tarifsXLS.read(require("../data/products/gpi/form/tarifs.xlsx"));
        await this.propositionPDF.read(require("../data/products/gpi/form/proposition.pdf"));
        await this.adhesionPDF.read(require("../data/products/gpi/form/adhesion.pdf"));
        await this.readCourrierPDF(advisor);
        super.init(advisor, customer, quotation);
    }

    public getDico() {
        return dicoGPI;
    }

    public IADGuaranteeItems(): Item[] {
        this.tarifsXLS.setActiveSheet("DC IAD");
        let items = this.replaceItemsLabel(this.getItems("B4:AB4", true), ".00", "");
        return this.replaceItemsLabel(items, ",", " ").slice(2);
    }

    public ITTFranchiseItems(): Item[] {
        this.tarifsXLS.setActiveSheet("ITT IPT");
        return this.getItems("B4:G4", false);
    }

    public ITTGuaranteeItems(): Item[] {
        if (this.product && this.product.IADGuarantee) {
            this.tarifsXLS.setActiveSheet("DC IAD");
            let values = [];
            if (this.product.IADGuarantee >= 15000) {
                const ijMax = this.tarifsXLS.findProductInRanges(
                    "F4:AB4",
                    this.product.IADGuarantee,
                    "A5:A6",
                    "IJ maximale"
                );
                if (ijMax) {
                    for (let i = 15; i <= ijMax.v; i = i + 5) {
                        values.push({
                            name: i + "",
                            label: i + " €",
                        });
                    }
                }
                return values;
            }
        }
        return [];
    }

    public getIADContribution(): number {
        if (this.product && this.product.IADGuarantee) {
            this.tarifsXLS.setActiveSheet("DC IAD");
            const age = this.getAgeAtEffectiveDate();
            let stringAge = age + " ans";
            if (age < 16) {
                return null;
            } else if (age >= 16 && age <= 19) {
                stringAge = "16 ans - 19 ans";
            } else if (age >= 20 && age <= 27) {
                stringAge = "20 ans - 27 ans";
            } else if (age >= 28 && age <= 31) {
                stringAge = "28 ans - 31 ans";
            } else if (age >= 32 && age <= 33) {
                stringAge = "32 ans - 33 ans";
            }
            const result = this.tarifsXLS.findProductInRanges("B4:AB4", this.product.IADGuarantee, "A7:A52", stringAge);
            if (!result) {
                throw new Error("L'age de l'assuré n'est pas pris en charge pour cette prestation");
            } else {
                return result.v;
            }
        }
        return null;
    }

    public getAccidentContribution(): number {
        if (this.product && this.product.IADGuarantee >= 15000) {
            return this.product.IADGuarantee / 1000;
        }
        return null;
    }

    public getITTContribution(): number {
        if (
            this.product &&
            this.product.ITTFranchise &&
            this.product.ITTGuarantee &&
            this.product.IADGuarantee >= 15000
        ) {
            this.tarifsXLS.setActiveSheet("ITT IPT");
            const age = this.getAgeAtEffectiveDate();
            const result = this.tarifsXLS.findProductInRanges("B4:G4", this.product.ITTFranchise, "A5:A56", age);
            if (result) {
                return (result.v * this.product.ITTGuarantee) / 100;
            }
            return null;
        }
        return null;
    }

    public getIPTContribution(): number {
        const product = this.quotation.product as ProductGPI;
        if (product && product.ITTGuarantee && product.IADGuarantee >= 15000) {
            this.tarifsXLS.setActiveSheet("ITT IPT");
            const age = this.getAgeAtEffectiveDate();
            const result = this.tarifsXLS.findProductInRanges("I4:I4", "IPT", "A5:A56", age);
            if (result) {
                return (result.v * product.ITTGuarantee) / 100;
            }
            return null;
        }
        return null;
    }

    public getIPTGuarantee(): number {
        const product = this.quotation.product as ProductGPI;
        if (product && product.ITTGuarantee) {
            return product.ITTGuarantee * 365;
        }
        return null;
    }

    public getContribution(): number {
        if (this.product) {
            let totalAmount = 0;

            if (this.product.IADContribution) {
                totalAmount += this.product.IADContribution;
            }

            if (this.product.isAccident && this.product.accidentContribution) {
                totalAmount += this.product.accidentContribution;
            }

            if (this.product.isITT && this.product.ITTContribution) {
                totalAmount += this.product.ITTContribution + this.product.IPTContribution;
            }

            return totalAmount;
        }
        return null;
    }

    public getPeriodicContribution(): number {
        switch (this.product.samplingInterval) {
            case "month":
                return this.round(this.product.contribution / 12);
            case "trimester":
                return this.round(this.product.contribution / 4);
            case "semester":
                return this.round(this.product.contribution / 2);
            case "year":
                return this.round(this.product.contribution);
            default:
                return null;
        }
    }

    public async readCourrierPDF(advisor: User) {
        if (advisor.company.name === "ag2r") {
            await this.courrierAdhesionPDF.read(
                require("../data/products/common/form/courrier_accompagnement_adhesion_GPI_GO.pdf")
            );
            await this.courrierPropositionPDF.read(
                require("../data/products/common/form/courrier_accompagnement_proposition_GPI_GO.pdf")
            );
        } else if (advisor.company.name === "viasante") {
            await this.courrierAdhesionPDF.read(
                require("../data/products/common/form/courrier_accompagnement_adhesion_GPI_GO_viasante.pdf")
            );
            await this.courrierPropositionPDF.read(
                require("../data/products/common/form/courrier_accompagnement_proposition_GPI_GO_viasante.pdf")
            );
        }
    }
    protected getPDFields() {
        const postFix = "";
        return {
            ...this.getAdvisorPDFFields(),
            ...this.getCustomerPDFFields(),

            // Date d'effet souhaitée

            ["effectiveDate" + postFix]: moment(this.quotation.effectiveDate).format("DDMMYYYY"),

            // Garanties

            product_IADGuarantee: this.product.IADGuarantee,
            product_IADAnnualContribution: this.product.IADContribution ? this.product.IADContribution.toFixed(2) : "",
            product_isAccident: this.product.isAccident,
            product_accidentGuarantee: this.product.accidentGuarantee ? this.product.accidentGuarantee : "",
            product_accidentAnnualContribution: this.product.accidentContribution
                ? this.product.accidentContribution.toFixed(2)
                : "",
            product_isITT: this.product.isITT,
            product_ITTDailyGuarantee: this.product.ITTGuarantee ? this.product.ITTGuarantee : "",
            product_ITTAnnualContribution: this.product.ITTContribution ? this.product.ITTContribution.toFixed(2) : "",
            product_ITTFranchise_15: this.product.ITTFranchise && this.product.ITTFranchise.toString() === "15",
            product_ITTFranchise_30: this.product.ITTFranchise && this.product.ITTFranchise.toString() === "30",
            product_ITTFranchise_42: this.product.ITTFranchise && this.product.ITTFranchise.toString() === "42",
            product_ITTFranchise_60: this.product.ITTFranchise && this.product.ITTFranchise.toString() === "60",
            product_ITTFranchise_90: this.product.ITTFranchise && this.product.ITTFranchise.toString() === "90",
            product_ITTFranchise_365: this.product.ITTFranchise && this.product.ITTFranchise.toString() === "365",
            product_IPTAnnualGuarantee: this.product.IPTGuarantee ? this.product.IPTGuarantee : "",
            product_IPTAnnualContribution: this.product.IPTContribution ? this.product.IPTContribution.toFixed(2) : "",
            product_annualContribution: this.product.contribution ? this.product.contribution.toFixed(2) : "",

            // Cotisations

            product_periodicContribution: this.getPeriodicContribution(),
            product_samplingInterval_year: this.product.samplingInterval === "year",
            product_samplingInterval_semester: this.product.samplingInterval === "semester",
            product_samplingInterval_trimester: this.product.samplingInterval === "trimester",
            product_samplingInterval_month: this.product.samplingInterval === "month",
            product_samplingDate_08: this.product.samplingDate === "08",
            product_samplingDate_15: this.product.samplingDate === "15",

            // TODO ?

            is_advisor_vendorCode: this.advisor.vendorCode, // TODO
            is_advisor_bringerCode: this.advisor.bringerCode, // TODO

            // Beneficiaries

            ...this.getProductBeneficiariesPDFFields(),
        };
    }

    // Override Date

    public getAgeAtDate(birthdate: string, date: string): number {
        return moment(date).year() - moment(birthdate).year();
    }
}



// WEBPACK FOOTER //
// ./src/helper/ProductGPI.helper.ts